import apiClient from '../apiClient';
import { 
  ApproveMeetingRequest, 
  RejectMeetingRequest, 
  EditMeetingRequest,
  MeetingsResponse,
  MeetingResponse,
  UpdateUserRoleRequest,
  UpdateUserStatusRequest,
  UpdateUserNotesRequest,
  UserListResponse,
  UserDetailResponse,
  AdminDashboardStats
} from '../../types';

const ENDPOINTS = {
  // Dashboard
  DASHBOARD_STATS: '/admin/dashboard/stats',
  
  // Meetings
  FILTERED_MEETINGS: '/admin/meetings',
  PENDING_MEETINGS: '/admin/meetings/pending',
  MEETING_DETAIL: '/admin/meetings',
  APPROVE_MEETING: '/admin/meetings/:id/approve',
  REJECT_MEETING: '/admin/meetings/:id/reject',
  CANCEL_MEETING: '/admin/meetings/:id/cancel',
  EDIT_MEETING: '/admin/meetings/:id',
  CREATE_MEETING_FOR_USER: '/admin/meetings/create',
  
  // Users
  USERS: '/admin/users',
  USER_DETAIL: '/admin/users/:id',
  USER_ROLE: '/admin/users/:id/role',
  USER_STATUS: '/admin/users/:id/status',
  USER_NOTES: '/admin/users/:id/notes',
  CREATE_USER: '/admin/users/create',
  SEARCH_USERS: '/admin/users/search',
  
  // Cache
  INVALIDATE_CACHE: '/admin/cache/invalidate'
};

// Cache for API responses
const responseCache = new Map<string, { data: any, timestamp: number }>();

// Improved cache TTLs - longer to reduce API calls
const DEFAULT_CACHE_TTL = 60 * 1000; // 1 minute default
const DASHBOARD_CACHE_TTL = 2 * 60 * 1000; // 2 minutes for dashboard
const PENDING_MEETINGS_CACHE_TTL = 30 * 1000; // 30 seconds for pending meetings
const MAX_CACHE_AGE = 5 * 60 * 1000; // 5 minutes max cache age

// Track which tabs have been updated by socket events
// Store with priority: true = needs immediate refresh, false = normal refresh
const updatedTabs = new Map<string, boolean>();

// Track in-flight requests to prevent duplicate API calls
const pendingRequests = new Map<string, Promise<any>>();

// Track the last time each tab was accessed
const tabAccessTimes = new Map<string, number>();

// Track when the last API call was made for each endpoint
const lastApiCallTimes = new Map<string, number>();
const MIN_API_CALL_INTERVAL = 2000; // Minimum 2 seconds between identical API calls

// Store refresh callbacks for real-time updates
const refreshCallbacks = new Map<string, () => void>();

// Set the currently active tab for optimization
let activeTab: string | null = null;
export const setActiveTab = (tab: string): void => {
  activeTab = tab;
  tabAccessTimes.set(tab, Date.now());
};

export const getActiveTab = (): string | null => {
  return activeTab;
};

// Register a callback to refresh data when needed
export const registerRefreshCallback = (status: string, callback: () => void): void => {
  refreshCallbacks.set(status, callback);
  console.log(`Registered refresh callback for status: ${status}`);
};

// New function to check if cache is valid and not stale
export const isCacheValid = (cacheKey: string, maxAge = DEFAULT_CACHE_TTL): boolean => {
  const cached = responseCache.get(cacheKey);
  const now = Date.now();
  
  // If this tab has been marked as updated, cache is invalid
  const tabMatch = cacheKey.match(/admin:meetings:([^:]+):/);
  if (tabMatch && tabMatch[1] && updatedTabs.has(tabMatch[1])) {
    const highPriority = updatedTabs.get(tabMatch[1]);
    console.log(`Cache for ${cacheKey} is invalid due to socket events (priority: ${highPriority ? 'high' : 'normal'})`);
    // Clear this tab from the updated list once we've detected it
    updatedTabs.delete(tabMatch[1]);
    
    // If there's a registered callback for this tab and it's high priority,
    // trigger it immediately 
    if (highPriority && tabMatch[1] && refreshCallbacks.has(tabMatch[1])) {
      console.log(`Triggering immediate refresh for ${tabMatch[1]}`);
      setTimeout(() => {
        const callback = refreshCallbacks.get(tabMatch[1]);
        if (callback) callback();
      }, 0);
    }
    
    return false;
  }
  
  // Check for hard expiry
  if (cached && now - cached.timestamp < maxAge) {
    console.log(`Cache is still valid for ${cacheKey}`);
    return true;
  }
  
  // If cache is older than MAX_CACHE_AGE, always invalidate
  if (cached && now - cached.timestamp > MAX_CACHE_AGE) {
    console.log(`Cache for ${cacheKey} exceeds maximum age`);
    return false;
  }
  
  // For inactive tabs, extend cache lifetime
  if (tabMatch && tabMatch[1] && activeTab && tabMatch[1] !== activeTab) {
    // If this is not the active tab, extend cache TTL by 2x
    if (cached && now - cached.timestamp < maxAge * 2) {
      console.log(`Extended cache validity for inactive tab ${tabMatch[1]}`);
      return true;
    }
  }
  
  console.log(`Cache is stale or not found for ${cacheKey}`);
  return false;
};

// Mark tab as updated via socket events
export const markTabAsUpdated = (status: string, highPriority: boolean = false): void => {
  if (!status) return; // Skip if status is null or undefined
  
  // High priority updates always mark the tab and trigger immediate refresh
  if (highPriority) {
    updatedTabs.set(status, true);
    console.log(`Marked tab ${status} as needing IMMEDIATE refresh due to socket events`);
    
    // Trigger refresh callback immediately if this is the active tab
    if (status === activeTab && refreshCallbacks.has(status)) {
      console.log(`Triggering immediate refresh for active tab ${status}`);
      const callback = refreshCallbacks.get(status);
      if (callback) {
        setTimeout(callback, 0);
      }
    }
    return;
  }
  
  // Normal priority updates follow usual rules
  const now = Date.now();
  const lastAccessed = tabAccessTimes.get(status) || 0;
  const isRecentlyAccessed = (now - lastAccessed) < 5000;
  
  if (status === activeTab || !isRecentlyAccessed) {
    updatedTabs.set(status, false);
    console.log(`Marked tab ${status} as needing refresh due to socket events`);
  } else {
    console.log(`Skipped marking inactive tab ${status} as it was recently refreshed`);
  }
};

// Deduplication for API calls
const deduplicatedApiCall = async <T>(
  cacheKey: string,
  apiCall: () => Promise<T>
): Promise<T> => {
  // Check if there's already a pending request for this key
  if (pendingRequests.has(cacheKey)) {
    console.log(`Reusing in-flight request for ${cacheKey}`);
    return pendingRequests.get(cacheKey)!;
  }
  
  // Create and store the promise
  const promise = apiCall();
  pendingRequests.set(cacheKey, promise);
  
  try {
    // Wait for the result
    const result = await promise;
    return result;
  } finally {
    // Clean up after the request completes
    pendingRequests.delete(cacheKey);
  }
};

// Helper function to get cached data or make API call
const getCachedOrFetch = async <T>(
  cacheKey: string, 
  apiCall: () => Promise<T>,
  ttl: number = DEFAULT_CACHE_TTL,
  forceRefresh: boolean = false
): Promise<T> => {
  const now = Date.now();
  const cached = responseCache.get(cacheKey);
  
  // Use cache if not forcing refresh and it's valid
  if (!forceRefresh && cached && now - cached.timestamp < ttl) {
    console.log(`Using cached data for ${cacheKey}`);
    return cached.data;
  }
  
  // Rate limit identical API calls
  const apiCallKey = cacheKey.split(':').slice(0, 2).join(':'); // Get the base part of the key
  const lastCallTime = lastApiCallTimes.get(apiCallKey) || 0;
  const timeSinceLastCall = now - lastCallTime;
  
  if (timeSinceLastCall < MIN_API_CALL_INTERVAL && pendingRequests.has(cacheKey)) {
    console.log(`Rate limiting API call for ${apiCallKey}, using in-flight request`);
    return pendingRequests.get(cacheKey)!;
  }
  
  // Make the actual API call with deduplication
  lastApiCallTimes.set(apiCallKey, now);
  const data = await deduplicatedApiCall(cacheKey, apiCall);
  
  // Cache the result
  responseCache.set(cacheKey, { data, timestamp: now });
  console.log(`Cached new data for ${cacheKey}`);
  
  return data;
};

// Helper to invalidate cache by prefix
export const invalidateCache = async (prefix: string): Promise<boolean> => {
  // First invalidate local client-side cache
  let count = 0;
  for (const key of responseCache.keys()) {
    if (key.startsWith(prefix)) {
      responseCache.delete(key);
      count++;
    }
  }
  console.log(`Invalidated ${count} local cache entries with prefix ${prefix}`);
  
  // Update tracking for status tabs that need refresh
  const statusMatch = prefix.match(/admin:meetings:([^:]+):/);
  if (statusMatch && statusMatch[1]) {
    markTabAsUpdated(statusMatch[1], true); // Mark with high priority
  }
  
  // Debounced server-side cache invalidation
  const now = Date.now();
  const lastInvalidateTime = lastApiCallTimes.get(`invalidate:${prefix}`) || 0;
  
  // Don't invalidate server-side cache too frequently
  if (now - lastInvalidateTime < 5000) {
    console.log(`Skipping server-side cache invalidation for ${prefix} (too frequent)`);
    return true;
  }
  
  // Then invalidate server-side cache by making API call
  try {
    lastApiCallTimes.set(`invalidate:${prefix}`, now);
    const response = await apiClient.post(ENDPOINTS.INVALIDATE_CACHE, { pattern: prefix });
    console.log(`Server cache invalidation response:`, response.data);
    return response.data.success === true;
  } catch (error) {
    console.error('Error invalidating server cache:', error);
    return false;
  }
};

// Helper to invalidate meeting status-specific caches
export const invalidateMeetingStatusCache = async (oldStatus: string, newStatus: string): Promise<boolean> => {
  // Mark both statuses and 'all' tab as needing refresh
  markTabAsUpdated(oldStatus, true); // with high priority
  markTabAsUpdated(newStatus, true); // with high priority
  markTabAsUpdated('all', true);     // with high priority
  
  // Invalidate specific status caches locally
  invalidateCache(`admin:meetings:${oldStatus}:`);
  invalidateCache(`admin:meetings:${newStatus}:`);
  invalidateCache('admin:meetings:all:');
  console.log(`Invalidated status-specific caches for ${oldStatus} and ${newStatus}`);
  
  // Debounce server-side invalidation
  const now = Date.now();
  const lastInvalidateTime = lastApiCallTimes.get(`invalidate:status:${oldStatus}:${newStatus}`) || 0;
  
  if (now - lastInvalidateTime < 5000) {
    console.log(`Skipping server-side status cache invalidation (too frequent)`);
    return true;
  }
  
  // Also invalidate on server - but debounced
  try {
    lastApiCallTimes.set(`invalidate:status:${oldStatus}:${newStatus}`, now);
    
    // Use a combined pattern that matches all three patterns instead of an array
    const pattern = `admin:meetings:(${oldStatus}|${newStatus}|all):*`;
    
    // Make a single API call with the combined pattern
    const response = await apiClient.post(ENDPOINTS.INVALIDATE_CACHE, { pattern });
    
    console.log(`Server cache invalidation response:`, response.data);
    return response.data.success === true;
  } catch (error) {
    console.error('Error invalidating server status caches:', error);
    return false;
  }
};

// Helper function to safely get meeting ID regardless of property name
const getMeetingIdentifier = (meetingData: any): string | null => {
  // Check if the data is an array (like in admin:meeting-created event)
  if (Array.isArray(meetingData) && meetingData.length > 0) {
    // If it's an array, use the first item
    const meeting = meetingData[0];
    // If there's a meeting property in the object, extract that
    if (meeting && meeting.meeting && typeof meeting.meeting === 'object') {
      meetingData = meeting.meeting;
    } else {
      meetingData = meeting;
    }
  } else if (meetingData && meetingData.meeting && typeof meetingData.meeting === 'object') {
    // If it's an object with a meeting property
    meetingData = meetingData.meeting;
  }
  
  // Now try to find an identifier in the meeting object
  if (meetingData && typeof meetingData === 'object') {
    // Check common ID field names
    const possibleIds = ['_id', 'meetingId', 'id'];
    for (const idField of possibleIds) {
      if (idField in meetingData && typeof meetingData[idField] === 'string') {
        return meetingData[idField];
      }
    }
    
    // Log all properties for debugging
    console.log('Meeting object properties:', Object.keys(meetingData));
  }
  
  return null;
};

// Function to update meeting cache directly with socket data
export const updateMeetingCache = async (
  newMeetingData: any,
  currentPage: number = 1,
  limit: number = 10
): Promise<MeetingsResponse | null> => {
  // Track last call to prevent spam
  const now = Date.now();
  const lastUpdateTime = lastApiCallTimes.get('updateMeetingCache') || 0;
  
  if (now - lastUpdateTime < 1000) {
    console.log(`Skipping cache update, too frequent (< 1s)`);
    return null;
  }
  
  lastApiCallTimes.set('updateMeetingCache', now);
  console.log(`Attempting to update cache with new meeting data`);
  
  // Extract meeting status for relevance checking
  let extractedMeetingData = newMeetingData;
  if (Array.isArray(newMeetingData) && newMeetingData.length > 0) {
    extractedMeetingData = newMeetingData[0];
  }
  if (extractedMeetingData && extractedMeetingData.meeting) {
    extractedMeetingData = extractedMeetingData.meeting;
  }
  
  // Check if this meeting update is relevant to active tab
  const meetingStatus = extractedMeetingData?.status || 'pending'; // Default to pending for new meetings
  
  // Always trigger refresh of affected tabs with high priority
  markTabAsUpdated(meetingStatus, true);
  markTabAsUpdated('all', true);
  
  // Update all caches for affected tabs
  const relevantTabs = ['all', meetingStatus];
  
  // Also update active tab if not already included
  if (activeTab && !relevantTabs.includes(activeTab)) {
    relevantTabs.push(activeTab);
  }
  
  // Update only relevant cache keys
  let updatedResponse: MeetingsResponse | null = null;
  
  for (const status of relevantTabs) {
    if (status) { // Add null check here
      const cacheKey = `admin:meetings:${status}:${currentPage}:${limit}`;
      const result = await updateSingleTabCache(cacheKey, newMeetingData, limit);
      
      if (result && !updatedResponse) {
        updatedResponse = responseCache.get(cacheKey)?.data as MeetingsResponse;
      }
    }
  }
  
  return updatedResponse;
};

// Helper to update a single tab's cache
const updateSingleTabCache = async (
  cacheKey: string,
  newMeetingData: any,
  limit: number = 10
): Promise<boolean> => {
  const cached = responseCache.get(cacheKey);
  
  // If we don't have cached data, skip update (will be fetched when needed)
  if (!cached) {
    return false;
  }
  
  const cachedData = cached.data as MeetingsResponse;
  
  // Extract the actual meeting object from the socket data
  let actualMeeting = newMeetingData;
  
  // Handle array format from socket
  if (Array.isArray(newMeetingData) && newMeetingData.length > 0) {
    actualMeeting = newMeetingData[0];
  }
  
  // Handle nested meeting object
  if (actualMeeting && actualMeeting.meeting) {
    actualMeeting = actualMeeting.meeting;
  }
  
  // Get meeting ID from the new meeting data
  const newMeetingId = getMeetingIdentifier(actualMeeting);
  
  if (!newMeetingId) {
    console.log('Unable to identify meeting ID in the socket data');
    return false;
  }
  
  // Extract tab name from cache key
  const tabMatch = cacheKey.match(/admin:meetings:([^:]+):/);
  const tabName = tabMatch ? tabMatch[1] : null;
  
  // Check if this is a new meeting (not already in our list)
  const isNewMeeting = !cachedData.meetings.some(meeting => {
    const meetingId = getMeetingIdentifier(meeting);
    return meetingId === newMeetingId;
  });
  
  if (isNewMeeting) {
    console.log(`Adding new meeting ${newMeetingId} to cache for ${cacheKey}`);
    
    // Create updated meetings list with new meeting at the beginning
    // Since pending meetings are usually sorted newest first
    const updatedMeetings = [actualMeeting, ...cachedData.meetings];
    
    // If we have more meetings than the limit, remove the last one
    if (updatedMeetings.length > limit) {
      updatedMeetings.pop();
    }
    
    // Update pagination count if needed
    const updatedPagination = {
      ...cachedData.pagination,
      total: cachedData.pagination.total + 1,
      // Pages might need to be recalculated if total/limit changed
      pages: Math.ceil((cachedData.pagination.total + 1) / limit)
    };
    
    // Create the updated response
    const updatedResponse: MeetingsResponse = {
      ...cachedData,
      meetings: updatedMeetings,
      pagination: updatedPagination
    };
    
    // Update the cache with fresh timestamp
    responseCache.set(cacheKey, { 
      data: updatedResponse, 
      timestamp: Date.now() 
    });
    
    // Trigger immediate refresh if this is for active tab
    if (tabName && tabName === activeTab && refreshCallbacks.has(tabName)) {
      console.log(`Triggering refresh for ${tabName} due to new meeting`);
      const callback = refreshCallbacks.get(tabName);
      if (callback) setTimeout(callback, 10);
    }
    
    return true;
  } else {
    // If the meeting already exists in our list, we should still update it
    console.log(`Updating existing meeting ${newMeetingId} in cache for ${cacheKey}`);
    
    // Find the meeting in our list and update it
    const updatedMeetings = cachedData.meetings.map(meeting => {
      const meetingId = getMeetingIdentifier(meeting);
      if (meetingId === newMeetingId) {
        // Return the updated meeting data
        return actualMeeting;
      }
      return meeting;
    });
    
    // Create the updated response with the same pagination
    const updatedResponse: MeetingsResponse = {
      ...cachedData,
      meetings: updatedMeetings
    };
    
    // Update the cache with fresh timestamp
    responseCache.set(cacheKey, { 
      data: updatedResponse, 
      timestamp: Date.now() 
    });
    
    // Trigger immediate refresh if this is for active tab
    if (tabName && tabName === activeTab && refreshCallbacks.has(tabName)) {
      console.log(`Triggering refresh for ${tabName} due to updated meeting`);
      const callback = refreshCallbacks.get(tabName);
      if (callback) setTimeout(callback, 10);
    }
    
    return true;
  }
};

// Helper function to invalidate user meeting cache
export const invalidateUserMeetingCache = async (userId: string): Promise<boolean> => {
  return invalidateCache(`user:${userId}:meetings`);
};

// Helper function to invalidate admin pending meetings cache
export const invalidateAdminPendingMeetingsCache = async (): Promise<boolean> => {
  return invalidateCache('admin:meetings:pending');
};

// Dashboard
export const getDashboardStats = async (): Promise<AdminDashboardStats> => {
  const cacheKey = 'admin:dashboard:stats';
  
  return getCachedOrFetch(cacheKey, async () => {
    console.log('API Call: getDashboardStats');
    const response = await apiClient.get(ENDPOINTS.DASHBOARD_STATS);
    console.log('Dashboard stats API response:', response.data);
    return response.data.stats;
  }, DASHBOARD_CACHE_TTL); // 2 minutes cache
};

// Get filtered meetings
export const getFilteredMeetings = async (
  status: string = 'pending',
  page: number = 1,
  limit: number = 10,
  skipCache: boolean = false
): Promise<MeetingsResponse> => {
  const cacheKey = `admin:meetings:${status}:${page}:${limit}`;
  
  // Track tab access time
  tabAccessTimes.set(status, Date.now());
  
  // Set this as the active tab
  setActiveTab(status);
  
  // Check if this tab has been updated by socket events
  if (updatedTabs.has(status)) {
    const highPriority = updatedTabs.get(status);
    console.log(`Tab ${status} was marked as updated (priority: ${highPriority ? 'high' : 'normal'}) - forcing refresh`);
    skipCache = true;
    updatedTabs.delete(status);
  }
  
  // Skip cache if requested
  if (skipCache) {
    console.log(`Skipping cache for ${cacheKey}`);
    
    // Make API call with parameters including timestamp to force fresh data
    console.log(`API Call: getFilteredMeetings (status: ${status}, page: ${page}, limit: ${limit}, skipCache: true)`);
    
    // Use deduplication to avoid parallel identical requests
    return deduplicatedApiCall(cacheKey, async () => {
      const response = await apiClient.get(ENDPOINTS.FILTERED_MEETINGS, {
        params: { 
          status, 
          page, 
          limit,
          skipCache: 'true',
          // Add a timestamp to force backend to not use its cache
          _t: Date.now()
        }
      });
      
      console.log('Filtered meetings API response:', response.data);
      
      // Only update cache if we get fresh data
      const responseData = response.data;
      const responseTimestamp = responseData.timestamp ? new Date(responseData.timestamp).getTime() : Date.now();
      
      // If response is fresh enough (within last 5 seconds), cache it
      if (Date.now() - responseTimestamp < 5000) {
        responseCache.set(cacheKey, { 
          data: responseData, 
          timestamp: Date.now() 
        });
      } else {
        console.log('Response data appears stale, not caching');
      }
      
      return responseData;
    });
  }
  
  // Determine appropriate TTL based on status
  let ttl = DEFAULT_CACHE_TTL;
  if (status === 'pending') {
    ttl = PENDING_MEETINGS_CACHE_TTL;
  }
  
  // Use cache for normal requests
  return getCachedOrFetch(
    cacheKey, 
    async () => {
      console.log(`API Call: getFilteredMeetings (status: ${status}, page: ${page}, limit: ${limit})`);
      
      const response = await apiClient.get(ENDPOINTS.FILTERED_MEETINGS, {
        params: { status, page, limit }
      });
      
      console.log('Filtered meetings API response:', response.data);
      return response.data;
    },
    ttl,
    skipCache
  );
};

// Meetings
export const getPendingMeetings = async (
  page: number = 1,
  limit: number = 10,
  skipCache: boolean = false
): Promise<MeetingsResponse> => {
  // Update active tab
  setActiveTab('pending');
  
  // Check if 'pending' tab has been updated via socket events
  if (updatedTabs.has('pending')) {
    const highPriority = updatedTabs.get('pending');
    console.log(`Pending tab was marked as updated (priority: ${highPriority ? 'high' : 'normal'}) - forcing refresh`);
    skipCache = true;
    updatedTabs.delete('pending');
  }
  
  // Use existing function with appropriate parameters
  return getFilteredMeetings('pending', page, limit, skipCache);
};

export const getMeetingById = async (id: string): Promise<MeetingResponse> => {
  const cacheKey = `admin:meeting:${id}`;
  
  return getCachedOrFetch(cacheKey, async () => {
    console.log(`API Call: getMeetingById (id: ${id})`);
    const response = await apiClient.get(`${ENDPOINTS.MEETING_DETAIL}/${id}`);
    console.log('Meeting detail API response:', response.data);
    return response.data;
  });
};

export const approveMeeting = async (
  id: string, 
  data: ApproveMeetingRequest
): Promise<MeetingResponse> => {
  console.log(`API Call: approveMeeting (id: ${id})`, data);
  const response = await apiClient.put(
    ENDPOINTS.APPROVE_MEETING.replace(':id', id), 
    data
  );
  console.log('Approve meeting API response:', response.data);
  
  // Invalidate related caches
  await invalidateCache('admin:meetings:');
  await invalidateCache(`admin:meeting:${id}`);
  await invalidateCache('admin:dashboard:');
  
  // Explicitly invalidate status-specific caches
  await invalidateMeetingStatusCache('pending', 'approved');
  
  return response.data;
};

export const rejectMeeting = async (
  id: string, 
  data: RejectMeetingRequest
): Promise<MeetingResponse> => {
  console.log(`API Call: rejectMeeting (id: ${id})`, data);
  const response = await apiClient.put(
    ENDPOINTS.REJECT_MEETING.replace(':id', id), 
    data
  );
  console.log('Reject meeting API response:', response.data);
  
  // Invalidate related caches
  await invalidateCache('admin:meetings:');
  await invalidateCache(`admin:meeting:${id}`);
  await invalidateCache('admin:dashboard:');
  
  // Explicitly invalidate status-specific caches
  await invalidateMeetingStatusCache('pending', 'rejected');
  
  return response.data;
};

export const cancelMeeting = async (
  id: string,
  reason: string
): Promise<any> => {
  console.log(`API Call: cancelMeeting (id: ${id}, reason: ${reason})`);
  const response = await apiClient.put(
    ENDPOINTS.CANCEL_MEETING.replace(':id', id), 
    { reason }
  );
  console.log('Cancel meeting API response:', response.data);
  
  // Invalidate related caches
  await invalidateCache('admin:meetings:');
  await invalidateCache(`admin:meeting:${id}`);
  await invalidateCache('admin:dashboard:');
  
  // Explicitly invalidate status-specific caches
  const previousStatus = response.data.previousStatus || 'approved';
  await invalidateMeetingStatusCache(previousStatus, 'cancelled');
  
  return response.data;
};

export const editMeeting = async (
  id: string, 
  data: EditMeetingRequest
): Promise<MeetingResponse> => {
  console.log(`API Call: editMeeting (id: ${id})`, data);
  const response = await apiClient.put(
    ENDPOINTS.EDIT_MEETING.replace(':id', id), 
    data
  );
  console.log('Edit meeting API response:', response.data);
  
  // Invalidate related caches
  await invalidateCache('admin:meetings:');
  await invalidateCache(`admin:meeting:${id}`);
  await invalidateCache('admin:dashboard:');
  
  // If approving as part of edit, also invalidate status-specific caches
  if (data.approve) {
    await invalidateMeetingStatusCache('pending', 'approved');
  }
  
  return response.data;
};

// Create meeting for user
export const createMeetingForUser = async (meetingData: {
  userId: string;
  title: string;
  description: string;
  date: string;
  time: string;
  coHosts?: Array<{name: string; phone: string}>;
}): Promise<any> => {
  console.log(`API Call: createMeetingForUser`, meetingData);
  const response = await apiClient.post(ENDPOINTS.CREATE_MEETING_FOR_USER, meetingData);
  console.log('Create meeting for user API response:', response.data);
  
  // Invalidate relevant caches
  await invalidateCache('admin:meetings:');
  await invalidateUserMeetingCache(meetingData.userId);
  await invalidateAdminPendingMeetingsCache();
  
  return response.data;
};

// Users - cache for longer since user data changes less frequently
export const getUsers = async (
  page: number = 1,
  limit: number = 10,
  filters: {
    search?: string;
    role?: string;
    isActive?: boolean;
  } = {}
): Promise<UserListResponse> => {
  const { search, role, isActive } = filters;
  const cacheKey = `admin:users:${page}:${limit}:${search || ''}:${role || ''}:${isActive}`;
  
  return getCachedOrFetch(cacheKey, async () => {
    console.log(`API Call: getUsers (page: ${page}, limit: ${limit})`, filters);
    const response = await apiClient.get(ENDPOINTS.USERS, {
      params: { page, limit, search, role, isActive }
    });
    console.log('User list API response:', response.data);
    return response.data;
  }, DEFAULT_CACHE_TTL * 2); // Cache user data for 2 minutes
};

export const getUserById = async (id: string): Promise<UserDetailResponse> => {
 const cacheKey = `admin:user:${id}`;
 
 return getCachedOrFetch(cacheKey, async () => {
   console.log(`API Call: getUserById (id: ${id})`);
   const response = await apiClient.get(ENDPOINTS.USER_DETAIL.replace(':id', id));
   console.log('User detail API response:', response.data);
   return response.data;
 }, DEFAULT_CACHE_TTL * 2); // Cache user details for 2 minutes
};

export const updateUserRole = async (
 id: string, 
 data: UpdateUserRoleRequest
): Promise<{ success: boolean; message: string; user: any }> => {
 console.log(`API Call: updateUserRole (id: ${id})`, data);
 const response = await apiClient.put(
   ENDPOINTS.USER_ROLE.replace(':id', id), 
   data
 );
 console.log('Update user role API response:', response.data);
 
 // Invalidate related caches
 await invalidateCache('admin:users:');
 await invalidateCache(`admin:user:${id}`);
 await invalidateCache('admin:dashboard:');
 
 return response.data;
};

export const updateUserStatus = async (
 id: string, 
 data: UpdateUserStatusRequest
): Promise<{ success: boolean; message: string; user: any }> => {
 console.log(`API Call: updateUserStatus (id: ${id})`, data);
 const response = await apiClient.put(
   ENDPOINTS.USER_STATUS.replace(':id', id), 
   data
 );
 console.log('Update user status API response:', response.data);
 
 // Invalidate related caches
 await invalidateCache('admin:users:');
 await invalidateCache(`admin:user:${id}`);
 await invalidateCache('admin:dashboard:');
 
 return response.data;
};

export const updateUserNotes = async (
 id: string, 
 data: UpdateUserNotesRequest
): Promise<{ success: boolean; message: string }> => {
 console.log(`API Call: updateUserNotes (id: ${id})`, data);
 const response = await apiClient.put(
   ENDPOINTS.USER_NOTES.replace(':id', id), 
   data
 );
 console.log('Update user notes API response:', response.data);
 
 // Invalidate related caches
 await invalidateCache(`admin:user:${id}`);
 
 return response.data;
};

// Create user without OTP
export const createUserAsAdmin = async (userData: {
  name: string;
  phone: string;
}): Promise<any> => {
  console.log(`API Call: createUserAsAdmin`, userData);
  const response = await apiClient.post(ENDPOINTS.CREATE_USER, userData);
  console.log('Create user API response:', response.data);
  
  // Invalidate user cache
  await invalidateCache('admin:users:');
  await invalidateCache('admin:dashboard:');
  
  return response.data;
};

// Search users
export const searchUsersByQuery = async (query: string, limit: number = 10): Promise<any> => {
  const cacheKey = `admin:users:search:${query}:${limit}`;
  
  return getCachedOrFetch(
    cacheKey,
    async () => {
      console.log(`API Call: searchUsersByQuery (query: ${query}, limit: ${limit})`);
      const response = await apiClient.get(ENDPOINTS.SEARCH_USERS, {
        params: { query, limit }
      });
      console.log('Search users API response:', response.data);
      return response.data;
    },
    5000 // Short TTL for search results (5 seconds)
  );
};
